import React, { useState } from "react";
import { Tooltip, message } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";
import styled from "styled-components";

const CopyButton = styled.span<{ $size?: "small" | "default" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #8c8c8c;
  font-size: ${props => props.$size === "small" ? "12px" : "14px"};
  padding: 2px;
  margin-left: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: 0;

  &:hover {
    color: #1890ff;
    background-color: rgba(24, 144, 255, 0.1);
  }
`;

const CopyWrapper = styled.span<{ $inline?: boolean }>`
  display: ${props => props.$inline ? "inline-flex" : "flex"};
  align-items: center;
  gap: 2px;

  &:hover ${CopyButton} {
    opacity: 1;
  }
`;

interface CopyToClipboardProps {
  text: string | null | undefined;
  children?: React.ReactNode;
  tooltipText?: string;
  successMessage?: string;
  size?: "small" | "default";
  inline?: boolean;
  showIcon?: boolean;
}

const CopyToClipboard: React.FC<CopyToClipboardProps> = ({
  text,
  children,
  tooltipText = "Copy to clipboard",
  successMessage = "Copied!",
  size = "default",
  inline = true,
  showIcon = true,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      message.success(successMessage, 1);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      message.error("Failed to copy");
    }
  };

  if (!text) {
    return <>{children}</>;
  }

  return (
    <CopyWrapper $inline={inline}>
      {children}
      {showIcon && (
        <Tooltip title={copied ? "Copied!" : tooltipText}>
          <CopyButton onClick={handleCopy} $size={size}>
            {copied ? <CheckOutlined style={{ color: "#52c41a" }} /> : <CopyOutlined />}
          </CopyButton>
        </Tooltip>
      )}
    </CopyWrapper>
  );
};

export default CopyToClipboard;
